import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePatientManagement } from "@/components/professional/dashboard/tabs/patients/usePatientManagement";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { PatientCard } from "@/components/professional/dashboard/tabs/patients/components/PatientCard";
import { usePatientStatusValidation } from "@/hooks/usePatientStatusValidation";

const MobileProfessionalPatients = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { validateStatus } = usePatientStatusValidation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [isAddingPatient, setIsAddingPatient] = useState(false);
  const [newPatientEmail, setNewPatientEmail] = useState("");
  const { addPatient } = usePatientManagement();

  // Chargement des relations patient
  const { data: patientRelations, isLoading, refetch } = useQuery({
    queryKey: ["professional-patients"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("professional_patients")
        .select(`
          *,
          patient:profiles!patient_id(*)
        `)
        .eq("professional_id", user.id);

      if (error) throw error;
      return data;
    },
  });

  // Chargement des messages pour le patient sélectionné
  const { data: messages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ["patient-messages", selectedPatientId],
    enabled: !!selectedPatientId,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !selectedPatientId) return [];

      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id(full_name)
        `)
        .or(`sender_id.eq.${selectedPatientId},receiver_id.eq.${selectedPatientId}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Filtrage des patients par nom
  const filteredPatients = patientRelations?.filter(relation => 
    relation.patient?.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Ajout d'un nouveau patient
  const handleAddPatient = async () => {
    if (!newPatientEmail) return;
    
    try {
      await addPatient.mutateAsync(newPatientEmail);
      setNewPatientEmail("");
      setIsAddingPatient(false);
      refetch();
    } catch (error) {
      // Error handled by the mutation's onError callback
    }
  };

  // Validation du statut patient
  const getValidatedPatientRelation = (relation: any) => {
    // Ensure the status is one of the valid PatientStatus values
    const validStatus = validateStatus(relation.status) ? relation.status : 'pending';
    
    return {
      ...relation,
      status: validStatus
    };
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-white px-3 py-2 flex items-center shadow-sm">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Retour</span>
        </Button>
        <h1 className="text-lg font-semibold text-gray-800">Mes patients</h1>
      </div>

      {/* Search and filters */}
      <div className="p-2 space-y-2">
        <div className="flex gap-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Rechercher un patient..."
              className="pl-7 py-1 h-9 text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Dialog open={isAddingPatient} onOpenChange={setIsAddingPatient}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-9 w-9 p-0">
                <UserPlus className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[90vw]">
              <DialogHeader>
                <DialogTitle>Ajouter un patient</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1">
                  <label htmlFor="patient-email" className="text-sm font-medium">
                    Email du patient
                  </label>
                  <Input
                    id="patient-email"
                    type="email"
                    value={newPatientEmail}
                    onChange={(e) => setNewPatientEmail(e.target.value)}
                    placeholder="patient@example.com"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddingPatient(false)} size="sm">
                  Annuler
                </Button>
                <Button onClick={handleAddPatient} size="sm">Ajouter</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Patient list with horizontal scroll */}
        <ScrollArea className="h-[calc(100vh-100px)]">
          <div className="space-y-2 pb-4 px-0.5 min-w-[320px]">
            {isLoading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="rounded-lg bg-white p-3 animate-pulse h-20" />
              ))
            ) : filteredPatients?.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-white rounded-lg p-3 text-sm">
                {searchQuery ? "Aucun patient trouvé" : "Vous n'avez pas encore de patients"}
              </div>
            ) : (
              filteredPatients?.map((relation) => (
                <div key={relation.id} className="space-y-2">
                  <PatientCard
                    relation={getValidatedPatientRelation(relation)}
                    messages={messages || []}
                    isLoadingMessages={isLoadingMessages}
                    onRefresh={refetch}
                    isSelected={selectedPatientId === relation.patient?.id}
                    onToggleDetails={() => setSelectedPatientId(
                      selectedPatientId === relation.patient?.id ? null : relation.patient?.id
                    )}
                  />
                </div>
              ))
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </div>
  );
};

export default MobileProfessionalPatients;
